import Head from "next/head"
import Link from "next/link"
import { BlitzPage, Routes } from "@blitzjs/next"
import { useRouter } from "next/router"
import { useQuery, useMutation } from "@blitzjs/rpc"
import Layout from "app/core/layouts/Layout"
import Suspense from "app/core/components/Suspense"
import getGeographies from "app/geographies/queries/getGeographies"
import deleteGeography from "app/geographies/mutations/deleteGeography"
import Container from "@mui/material/Container"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import TableContainer from "@mui/material/TableContainer"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Stack from "@mui/material/Stack"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"

export const GeographiesList = () => {
  const router = useRouter()
  const page = Number(router.query.page) || 0
  const [geographies, { refetch }] = useQuery(getGeographies, {
    orderBy: { name: "asc" },
  })
  const [deleteGeographyMutation] = useMutation(deleteGeography)

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Countries</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {geographies.map((geography) => (
            <TableRow key={geography.id} hover={true}>
              <TableCell>
                {geography.name}
                {geography.default ? ", Everywhere else" : ""}
              </TableCell>
              <TableCell>
                {geography.countries
                  .map((c) => c.name)
                  .sort()
                  .join(", ")}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <Stack direction="row" justifyContent="flex-end">
                  {!geography.default && (
                    <Link
                      href={Routes.EditGeographyPage({ geographyId: geography.id })}
                      legacyBehavior
                    >
                      <IconButton>
                        <EditIcon />
                      </IconButton>
                    </Link>
                  )}
                  {!geography.default && (
                    <IconButton
                      onClick={async () => {
                        if (window.confirm("This will be deleted")) {
                          await deleteGeographyMutation({ id: geography.id })
                          await refetch()
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const GeographiesPage: BlitzPage = () => {
  return (
    <>
      <Head>
        <title>Geographies</title>
      </Head>

      <Container maxWidth={false}>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ my: 4, flexGrow: 1 }} variant="h4" component="h2">
            Geographies
          </Typography>
          <Box>
            <Link href={Routes.NewGeographyPage()} legacyBehavior>
              <Button variant="contained" startIcon={<AddIcon />}>
                Create
              </Button>
            </Link>
          </Box>
        </Box>
        <Suspense>
          <GeographiesList />
        </Suspense>
      </Container>
    </>
  )
}

GeographiesPage.authenticate = true
GeographiesPage.getLayout = (page) => <Layout>{page}</Layout>

export default GeographiesPage
